import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCached, getCachedSync, setCached } from "@/lib/offline/persistentCache";
import { getIsOnline } from "@/hooks/useIsOnline";

// market_data_history is not in the generated Database type yet.
// TODO: regen types after the migration lands; until then read via `as any`.
export interface HistoryPoint {
  ts: string; // YYYY-MM-DD
  close: number;
}

// Last-known sparkline history is cached per ticker so the sparkline survives an offline/cold start
// (offline-resilience substrate). Non-confidential view data — safe for persistentCache.
const cacheKeyFor = (ticker: string) => `market.history.${ticker}`;

/**
 * Read recent daily history for a ticker from market_data_history.
 * Returns ascending-by-date close points for the last `days` calendar days.
 *
 * Offline-resilient: on a successful fetch the points are written through to the persistent cache;
 * when the fetch fails or rejects (e.g. offline) the last-known-good cached points are shown instead
 * of an empty sparkline. `fromCache` / `cachedAt` expose when the served points are stale.
 */
export function useMarketHistory(ticker: string | null | undefined, days = 90) {
  // Seed synchronously from the in-memory cache mirror so the sparkline paints instantly on a cold
  // offline start (mirror is hydrated at app startup). Falls back to [] when nothing is cached.
  const [points, setPoints] = useState<HistoryPoint[]>(() => {
    if (!ticker) return [];
    return getCachedSync<HistoryPoint[]>(cacheKeyFor(ticker))?.data ?? [];
  });
  const [loading, setLoading] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [cachedAt, setCachedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!ticker) {
      setPoints([]);
      setFromCache(false);
      setCachedAt(null);
      return;
    }
    let cancelled = false;
    setLoading(true);

    const key = cacheKeyFor(ticker);

    // Adopt any cached points immediately (covers ticker changes between renders) so we never flash
    // an empty sparkline while the network read is in flight.
    const seeded = getCachedSync<HistoryPoint[]>(key);
    if (seeded?.data?.length) {
      setPoints(seeded.data);
      setFromCache(true);
      setCachedAt(seeded.cachedAt);
    }

    const since = new Date();
    since.setDate(since.getDate() - days);
    const sinceStr = since.toISOString().slice(0, 10);

    // Fall back to whatever last-known-good history we have (async backend read, then sync mirror).
    const fallbackToCache = async () => {
      const cached = await getCached<HistoryPoint[]>(key);
      if (cancelled) return;
      if (cached?.data?.length) {
        setPoints(cached.data);
        setFromCache(true);
        setCachedAt(cached.cachedAt);
      }
      // If there is no cached history, leave whatever was seeded (possibly []) in place.
    };

    (async () => {
      try {
        // TODO: regen types — market_data_history not in generated Database yet.
        const { data, error } = await (supabase as any)
          .from("market_data_history")
          .select("ts, close")
          .eq("ticker", ticker)
          .gte("ts", sinceStr)
          .order("ts", { ascending: true });

        if (cancelled) return;
        if (!error && data) {
          const fresh: HistoryPoint[] = (data as any[])
            .filter((d) => d.close != null)
            .map((d) => ({ ts: String(d.ts), close: Number(d.close) }));
          setPoints(fresh);
          setFromCache(false);
          setCachedAt(null);
          // Write-through last-known-good (only when we actually got points, so a transient empty
          // result doesn't wipe a good cached sparkline). Fire-and-forget; never throws.
          if (fresh.length) void setCached(key, fresh);
        } else if (error) {
          // Offline / RLS / transient failure: prefer cached points over an empty sparkline.
          if (!getIsOnline()) {
            console.warn("useMarketHistory offline, showing cached history:", ticker);
          } else {
            console.error("useMarketHistory failed:", error);
          }
          await fallbackToCache();
        }
      } catch (e) {
        // .from() can reject outright when offline — fall back to cache rather than going empty.
        if (cancelled) return;
        if (!getIsOnline()) {
          console.warn("useMarketHistory offline, showing cached history:", ticker);
        } else {
          console.error("useMarketHistory threw:", e);
        }
        await fallbackToCache();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ticker, days]);

  return { points, loading, fromCache, cachedAt };
}
